import { Editor } from "@tiptap/react";

import { Box, useScaleFactor, useSpaceSize } from "arkh";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleProp,
  ViewStyle,
} from "react-native";
import RT, { RTProps, RTRef } from "./RT";
import { useDebouncedCallback } from "use-debounce";
import * as Sentry from "@sentry/react-native";

export type RichTextEditorProps = {
  initialContent?: string | null;
  initialUnformattedContent?: string | null;
  placeholder?: string;
  disabled?: boolean;
  externalSession?: boolean;

  onChange: (args: { body: string; formattedBody: string }) => void;
  fontMap: FontMap;
};
type FontMap = {
  "Inter-Regular": any;
  "Inter-Bold": any;
  "Inter-Italic": any;
  "Inter-SemiBoldItalic": any;
};
export const RichTextEditor = (props: RichTextEditorProps) => {
  const ref = useRef<RTRef>(null);

  useEffect(() => {
    if (props.externalSession && props.initialContent) {
      ref.current?.setContent(JSON.parse(props.initialContent));
    }
  }, [props.initialContent, props.externalSession]);

  const { onChange } = props;

  const onUpdate = useDebouncedCallback(({ editor }: { editor: Editor }) => {
    const formattedBody = JSON.stringify(editor.getJSON());
    if (formattedBody !== props.initialContent) {
      onChange({
        body: editor.getText(),
        formattedBody,
      });
    }
  }, 10);

  const scaleFactor = useScaleFactor();
  const padding = useSpaceSize("small");

  return (
    <Box
      spaceHorizontal="small"
      spaceLeft="medium"
      spaceTop="medium"
      style={{ flex: 1 }}
    >
      <ScrollView>
        <RichTextWrapperEditor
          ref={ref}
          scaleFactor={scaleFactor}
          fontMap={props.fontMap}
          style={{ minHeight: 440, paddingBottom: padding }}
          content={
            props.initialContent
              ? JSON.parse(props.initialContent)
              : props.initialUnformattedContent
          }
          placeholder={props.placeholder}
          editable={!props.disabled}
          onUpdate={onUpdate}
        />
      </ScrollView>
    </Box>
  );
};

export type RichTextViewerProps = {
  data?: string | null;
  unformattedData?: string | null;
  style?: StyleProp<ViewStyle>;
  fontMap: FontMap;
};
export const RichTextViewer = (props: RichTextViewerProps) => {
  const ref = useRef(null);
  const scaleFactor = useScaleFactor();
  if (!props.data) {
    return null;
  }

  return (
    <RichTextWrapper
      ref={ref}
      scaleFactor={scaleFactor}
      editable={false}
      fontMap={props.fontMap}
      key={props.data}
      content={JSON.parse(props.data)}
      style={props.style}
    />
  );
};

const RichTextWrapperEditor = ({
  style,
  ...props
}: RTProps & { style?: StyleProp<ViewStyle> }) => {
  const [loading, setLoading] = useState(Platform.OS !== "web");
  const scaleFactor = useScaleFactor();
  return (
    <>
      {loading ? <ActivityIndicator color="black" animating /> : null}
      <RT
        {...props}
        scaleFactor={scaleFactor}
        dom={{
          onLoadStart: () => {
            setLoading(true);
          },
          onLoadEnd: () => {
            setLoading(false);
          },
          onError: (error) => {
            Sentry.captureException(error);
          },
          matchContents: true,
        }}
      />
    </>
  );
};

const RichTextWrapper = ({
  style,
  ...props
}: RTProps & { style?: StyleProp<ViewStyle> }) => {
  const [loading, setLoading] = useState(Platform.OS !== "web");
  const [height, setHeight] = useState(1);
  const _setHeight = useCallback(
    async (v: number) => {
      setHeight(v);
    },
    [setHeight]
  );

  return (
    <>
      <RT
        {...props}
        onSizeChange={_setHeight}
        dom={{
          onLoadStart: () => {
            setLoading(true);
          },
          onLoadEnd: () => {
            setLoading(false);
          },
          onError: (error) => {
            Sentry.captureException(error);
          },
          scrollEnabled: false,
          androidLayerType: "software",

          style: [
            style,
            {
              minHeight: 1,
              height,
            },
          ],
        }}
      />
    </>
  );
};
